import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, MapPin, Search, X, Camera, ArrowLeft, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLogSheet } from "@/contexts/LogSheetContext";
import {
  searchGooglePlaces,
  upsertRestaurantFromPlace,
  type GooglePlace,
  type RestaurantRow,
} from "@/lib/google";
import { formatDistance } from "@/lib/distance";
import { FButton } from "./FButton";
import { FInput } from "./FInput";
import { checkAchievements, type UserAchievement } from "@/lib/achievementEngine";
import { BadgeReveal } from "./BadgeReveal";

// ---------------------------------------------------------------
// Types
// ---------------------------------------------------------------
type NearbyRow = {
  id: string;
  google_place_id: string | null;
  name: string;
  address: string | null;
  lat: number;
  lng: number;
  neighbourhood_id: string | null;
  city_id: string;
  price_tier: number | null;
  is_verified: boolean;
  visit_count: number;
  photo_url: string | null;
  distance_metres: number;
  city_name: string | null;
  neighbourhood_name: string | null;
  cuisine_names: string[] | null;
  cuisine_emojis: string[] | null;
};

type ConfirmedRestaurant = {
  id: string;
  name: string;
  address: string | null;
  city_name: string | null;
  neighbourhood_name: string | null;
  price_tier: number | null;
  photo_url: string | null;
  cuisine_names: string[];
  cuisine_emojis: string[];
  cuisine_type_ids: string[];
};

type DishRow = { id: string; name: string; cuisine_type_id: string | null };

type Step = 1 | 2 | 3 | 4 | 5;

// ---------------------------------------------------------------
// Shell
// ---------------------------------------------------------------
export function LogMealSheet() {
  const { open, prefillRestaurantId, closeSheet } = useLogSheet();
  const [step, setStep] = useState<Step>(1);
  const [restaurant, setRestaurant] = useState<ConfirmedRestaurant | null>(null);
  const [selectedDishIds, setSelectedDishIds] = useState<string[]>([]);
  const [suggestedDishNames, setSuggestedDishNames] = useState<string[]>([]);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [caption, setCaption] = useState("");
  const [loggedVisitId, setLoggedVisitId] = useState<string | null>(null);

  const reset = useCallback(() => {
    setStep(1);
    setRestaurant(null);
    setSelectedDishIds([]);
    setSuggestedDishNames([]);
    setPhotoFile(null);
    setCaption("");
    setLoggedVisitId(null);
  }, []);

  const handleClose = useCallback(() => {
    closeSheet();
    // Reset after close animation
    setTimeout(reset, 300);
  }, [closeSheet, reset]);

  // Lock body scroll when open
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  // Prefill: if opened with a restaurant id, fetch + jump to Step 2.
  useEffect(() => {
    if (!open || !prefillRestaurantId) return;
    let cancelled = false;
    (async () => {
      const [{ data: r }, meta, cuisineIds] = await Promise.all([
        supabase
          .from("restaurants")
          .select("id, name, address, price_tier, photo_url")
          .eq("id", prefillRestaurantId)
          .maybeSingle(),
        fetchRestaurantMeta(prefillRestaurantId),
        fetchCuisineIdsForRestaurant(prefillRestaurantId),
      ]);
      if (cancelled || !r) return;
      setRestaurant({
        id: r.id,
        name: r.name,
        address: r.address,
        city_name: meta.city_name,
        neighbourhood_name: meta.neighbourhood_name,
        price_tier: r.price_tier,
        photo_url: r.photo_url,
        cuisine_names: meta.cuisine_names,
        cuisine_emojis: meta.cuisine_emojis,
        cuisine_type_ids: cuisineIds,
      });
      setStep(2);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, prefillRestaurantId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end">
      {/* Backdrop */}
      <button
        aria-label="Close"
        onClick={handleClose}
        className="absolute inset-0 bg-black/40 animate-in fade-in duration-200"
      />

      {/* Sheet */}
      <div
        className="relative w-full max-h-[92vh] flex flex-col animate-in slide-in-from-bottom duration-300"
        style={{
          backgroundColor: "var(--colour-surface)",
          borderTopLeftRadius: "var(--radius-lg)",
          borderTopRightRadius: "var(--radius-lg)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {/* Drag handle */}
        <div className="pt-2.5 pb-1 flex justify-center shrink-0">
          <div
            className="w-10 h-1 rounded-full"
            style={{ backgroundColor: "var(--colour-border)" }}
          />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 h-12 shrink-0">
          {step > 1 && step < 5 ? (
            <button
              aria-label="Back"
              onClick={() => setStep((s) => (Math.max(1, s - 1) as Step))}
              className="w-11 h-11 -ml-3 flex items-center justify-center"
            >
              <ArrowLeft size={22} />
            </button>
          ) : (
            <span className="w-11" />
          )}
          <h2 className="text-[16px] font-semibold">
            {step === 1 && "Find restaurant"}
            {step === 2 && "Confirm"}
            {step === 3 && "What did you eat?"}
            {step === 4 && "Add a photo"}
            {step === 5 && "Logged"}
          </h2>
          <button
            aria-label="Close"
            onClick={handleClose}
            className="w-11 h-11 -mr-3 flex items-center justify-center"
          >
            <X size={22} />
          </button>
        </div>

        {/* Step bodies */}
        {step === 1 && (
          <Step1Find
            onSelect={(r) => {
              setRestaurant(r);
              setStep(2);
            }}
          />
        )}
        {step === 2 && restaurant && (
          <Step2Confirm
            restaurant={restaurant}
            onConfirm={() => setStep(3)}
            onWrong={() => setStep(1)}
          />
        )}
        {step === 3 && restaurant && (
          <Step3Dishes
            restaurant={restaurant}
            selectedIds={selectedDishIds}
            onChange={setSelectedDishIds}
            suggestedNames={suggestedDishNames}
            onChangeSuggestedNames={setSuggestedDishNames}
            onNext={() => setStep(4)}
            onSkip={() => {
              setSelectedDishIds([]);
              setSuggestedDishNames([]);
              setStep(4);
            }}
          />
        )}
        {step === 4 && restaurant && (
          <Step4Photo
            photoFile={photoFile}
            setPhotoFile={setPhotoFile}
            caption={caption}
            setCaption={setCaption}
            restaurant={restaurant}
            dishIds={selectedDishIds}
            suggestedDishNames={suggestedDishNames}
            onLogged={(visitId) => {
              setLoggedVisitId(visitId);
              setStep(5);
            }}
          />
        )}
        {step === 5 && restaurant && loggedVisitId && (
          <Step5Result
            visitId={loggedVisitId}
            restaurant={restaurant}
            dishIds={selectedDishIds}
            photoUrlsHint={null}
            onDone={handleClose}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Step 1 — Find restaurant
// ---------------------------------------------------------------
function Step1Find({
  onSelect,
}: {
  onSelect: (r: ConfirmedRestaurant) => void;
}) {
  const [tab, setTab] = useState<"near" | "search">("near");

  const handleNearbyPick = async (row: NearbyRow) => {
    const cuisineIds = await fetchCuisineIdsForRestaurant(row.id);
    onSelect({
      id: row.id,
      name: row.name,
      address: row.address,
      city_name: row.city_name,
      neighbourhood_name: row.neighbourhood_name,
      price_tier: row.price_tier,
      photo_url: row.photo_url,
      cuisine_names: row.cuisine_names ?? [],
      cuisine_emojis: row.cuisine_emojis ?? [],
      cuisine_type_ids: cuisineIds,
    });
  };

  const handleSupabasePick = async (row: RestaurantBasicRow) => {
    const cuisineIds = await fetchCuisineIdsForRestaurant(row.id);
    const meta = await fetchRestaurantMeta(row.id);
    onSelect({
      id: row.id,
      name: row.name,
      address: row.address,
      city_name: meta.city_name,
      neighbourhood_name: meta.neighbourhood_name,
      price_tier: row.price_tier,
      photo_url: row.photo_url,
      cuisine_names: meta.cuisine_names,
      cuisine_emojis: meta.cuisine_emojis,
      cuisine_type_ids: cuisineIds,
    });
  };

  const handleGooglePick = async (place: GooglePlace) => {
    const saved = await upsertRestaurantFromPlace(place);
    if (!saved) {
      toast.error("Could not save restaurant");
      return;
    }
    const meta = await fetchRestaurantMeta(saved.id);
    onSelect({
      id: saved.id,
      name: saved.name,
      address: saved.address,
      city_name: meta.city_name,
      neighbourhood_name: meta.neighbourhood_name,
      price_tier: saved.price_tier,
      photo_url: saved.photo_url,
      cuisine_names: meta.cuisine_names,
      cuisine_emojis: meta.cuisine_emojis,
      cuisine_type_ids: [],
    });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Tabs */}
      <div className="px-4 pt-1 pb-3 shrink-0">
        <div
          className="grid grid-cols-2 p-1 rounded-[var(--radius-md)]"
          style={{ backgroundColor: "var(--colour-surface-raised)" }}
        >
          <TabButton
            active={tab === "near"}
            onClick={() => setTab("near")}
            label="Near Me"
          />
          <TabButton
            active={tab === "search"}
            onClick={() => setTab("search")}
            label="Search"
          />
        </div>
      </div>

      {tab === "near" ? (
        <NearMeList onPick={handleNearbyPick} switchToSearch={() => setTab("search")} />
      ) : (
        <SearchList onPickSupabase={handleSupabasePick} onPickGoogle={handleGooglePick} />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className="h-10 rounded-[var(--radius-sm)] text-[14px] font-semibold transition-colors"
      style={{
        backgroundColor: active ? "var(--colour-surface)" : "transparent",
        color: active ? "var(--colour-text-primary)" : "var(--colour-text-secondary)",
        boxShadow: active ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
      }}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------
// Near Me list
// ---------------------------------------------------------------
function NearMeList({
  onPick,
  switchToSearch,
}: {
  onPick: (r: NearbyRow) => void;
  switchToSearch: () => void;
}) {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "denied" }
    | { kind: "empty" }
    | { kind: "error"; message: string }
    | { kind: "ready"; rows: NearbyRow[] }
  >({ kind: "loading" });

  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setState({ kind: "denied" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const { data, error } = await supabase.rpc("get_nearby_restaurants", {
          user_lat: latitude,
          user_lng: longitude,
          radius_metres: 500,
          result_limit: 20,
        });
        if (error) {
          setState({ kind: "error", message: error.message });
          return;
        }
        const rows = (data ?? []) as NearbyRow[];
        if (rows.length === 0) setState({ kind: "empty" });
        else setState({ kind: "ready", rows });
      },
      () => setState({ kind: "denied" }),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, []);

  if (state.kind === "loading") {
    return (
      <div className="flex-1 overflow-y-auto px-4 pb-6 space-y-2.5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-16 rounded-[var(--radius-md)] animate-pulse"
            style={{ backgroundColor: "var(--colour-surface-raised)" }}
          />
        ))}
      </div>
    );
  }

  if (state.kind === "denied") {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 pb-10">
        <MapPin size={32} className="text-[var(--colour-text-secondary)] mb-3" />
        <p className="text-[15px] text-[var(--colour-text-secondary)] mb-4">
          Location unavailable. Use Search to find your restaurant.
        </p>
        <FButton onClick={switchToSearch}>Open Search</FButton>
      </div>
    );
  }

  if (state.kind === "empty") {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 pb-10">
        <p className="text-[15px] text-[var(--colour-text-secondary)] mb-4">
          No restaurants found nearby. Try searching instead.
        </p>
        <FButton onClick={switchToSearch}>Open Search</FButton>
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div className="flex-1 flex items-center justify-center px-6 text-center">
        <p className="text-[14px] text-[var(--colour-text-secondary)]">
          {state.message}
        </p>
      </div>
    );
  }

  return (
    <ul className="flex-1 overflow-y-auto px-4 pb-6 space-y-2.5">
      {state.rows.map((r) => (
        <li key={r.id}>
          <NearbyRowButton row={r} onClick={() => onPick(r)} />
        </li>
      ))}
    </ul>
  );
}

function NearbyRowButton({
  row,
  onClick,
}: {
  row: NearbyRow;
  onClick: () => void;
}) {
  const emoji = row.cuisine_emojis?.[0] ?? "🍽️";
  const cuisine = row.cuisine_names?.[0] ?? "Restaurant";
  return (
    <button
      onClick={onClick}
      className="w-full text-left flex items-center gap-3 min-h-[64px] px-3 rounded-[var(--radius-md)] border"
      style={{
        borderColor: "var(--colour-border)",
        backgroundColor: "var(--colour-surface)",
      }}
    >
      <div
        className="w-11 h-11 shrink-0 rounded-[var(--radius-md)] flex items-center justify-center text-[22px]"
        style={{ backgroundColor: "var(--colour-surface-raised)" }}
      >
        {emoji}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-semibold truncate">{row.name}</div>
        <div className="text-[12px] text-[var(--colour-text-secondary)] truncate">
          {cuisine}
          {row.neighbourhood_name ? ` · ${row.neighbourhood_name}` : ""}
        </div>
      </div>
      <div className="text-[12px] text-[var(--colour-text-secondary)] shrink-0">
        {formatDistance(row.distance_metres)}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------
// Search list
// ---------------------------------------------------------------
type RestaurantBasicRow = {
  id: string;
  name: string;
  address: string | null;
  price_tier: number | null;
  photo_url: string | null;
  city_id: string;
};

function SearchList({
  onPickSupabase,
  onPickGoogle,
}: {
  onPickSupabase: (r: RestaurantBasicRow) => void;
  onPickGoogle: (p: GooglePlace) => void;
}) {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [supabaseHits, setSupabaseHits] = useState<RestaurantBasicRow[]>([]);
  const [googleHits, setGoogleHits] = useState<GooglePlace[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setSupabaseHits([]);
      setGoogleHits([]);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      const { data: dbRows } = await supabase
        .from("restaurants")
        .select("id, name, address, price_tier, photo_url, city_id")
        .ilike("name", `%${q}%`)
        .eq("is_active", true)
        .limit(10);
      const rows = (dbRows ?? []) as RestaurantBasicRow[];
      setSupabaseHits(rows);

      if (rows.length < 3) {
        let lat: number | undefined;
        let lng: number | undefined;
        if (user) {
          const { data: prof } = await supabase
            .from("profiles")
            .select("home_city_id")
            .eq("id", user.id)
            .maybeSingle();
          if (prof?.home_city_id) {
            const { data: city } = await supabase
              .from("cities")
              .select("lat, lng")
              .eq("id", prof.home_city_id)
              .maybeSingle();
            if (city) {
              lat = city.lat as number;
              lng = city.lng as number;
            }
          }
        }
        const places = await searchGooglePlaces(q, lat, lng);
        // Filter out any that already exist in supabase results
        const existingIds = new Set(rows.map((r) => r.id));
        const existingPlaceIds = new Set(rows.map((r) => (r as any).google_place_id).filter(Boolean));
        setGoogleHits(
          places.filter(
            (p) => !existingPlaceIds.has(p.place_id) && !existingIds.has(p.place_id),
          ),
        );
      } else {
        setGoogleHits([]);
      }
      setLoading(false);
    }, 400);
    return () => clearTimeout(handle);
  }, [query, user]);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="px-4 pb-3 shrink-0">
        <div className="relative">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--colour-text-secondary)]"
          />
          <FInput
            name="restaurant_search"
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search restaurants"
            className="pl-9"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-6">
        {loading && (
          <div className="space-y-2.5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-16 rounded-[var(--radius-md)] animate-pulse"
                style={{ backgroundColor: "var(--colour-surface-raised)" }}
              />
            ))}
          </div>
        )}

        {!loading && query.trim() && supabaseHits.length === 0 && googleHits.length === 0 && (
          <p className="text-center text-[14px] text-[var(--colour-text-secondary)] py-10">
            No matches.
          </p>
        )}

        {!loading && supabaseHits.length > 0 && (
          <ul className="space-y-2.5">
            {supabaseHits.map((r) => (
              <li key={r.id}>
                <button
                  onClick={() => onPickSupabase(r)}
                  className="w-full text-left flex items-center gap-3 min-h-[64px] px-3 rounded-[var(--radius-md)] border"
                  style={{
                    borderColor: "var(--colour-border)",
                    backgroundColor: "var(--colour-surface)",
                  }}
                >
                  <div
                    className="w-11 h-11 shrink-0 rounded-[var(--radius-md)] flex items-center justify-center text-[20px]"
                    style={{ backgroundColor: "var(--colour-surface-raised)" }}
                  >
                    🍽️
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] font-semibold truncate">{r.name}</div>
                    {r.address && (
                      <div className="text-[12px] text-[var(--colour-text-secondary)] truncate">
                        {r.address}
                      </div>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}

        {!loading && googleHits.length > 0 && (
          <>
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px" style={{ backgroundColor: "var(--colour-border)" }} />
              <span className="text-[11px] uppercase tracking-wider text-[var(--colour-text-secondary)]">
                More places
              </span>
              <div className="flex-1 h-px" style={{ backgroundColor: "var(--colour-border)" }} />
            </div>
            <ul className="space-y-2.5">
              {googleHits.map((p) => (
                <li key={p.place_id}>
                  <button
                    onClick={() => onPickGoogle(p)}
                    className="w-full text-left flex items-center gap-3 min-h-[64px] px-3 rounded-[var(--radius-md)] border"
                    style={{
                      borderColor: "var(--colour-border)",
                      backgroundColor: "var(--colour-surface)",
                    }}
                  >
                    <div
                      className="w-11 h-11 shrink-0 rounded-[var(--radius-md)] flex items-center justify-center text-[20px]"
                      style={{ backgroundColor: "var(--colour-surface-raised)" }}
                    >
                      📍
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[15px] font-semibold truncate">{p.name}</div>
                      {p.address && (
                        <div className="text-[12px] text-[var(--colour-text-secondary)] truncate">
                          {p.address}
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Step 2 — Confirm
// ---------------------------------------------------------------
function Step2Confirm({
  restaurant,
  onConfirm,
  onWrong,
}: {
  restaurant: ConfirmedRestaurant;
  onConfirm: () => void;
  onWrong: () => void;
}) {
  return (
    <div className="flex-1 overflow-y-auto px-4 pb-6">
      <div
        className="rounded-[var(--radius-lg)] overflow-hidden border"
        style={{
          borderColor: "var(--colour-border)",
          backgroundColor: "var(--colour-surface)",
        }}
      >
        {restaurant.photo_url ? (
          <img
            src={restaurant.photo_url}
            alt={restaurant.name}
            className="w-full h-44 object-cover"
          />
        ) : (
          <div
            className="w-full h-44 flex items-center justify-center text-[48px]"
            style={{ backgroundColor: "var(--colour-surface-raised)" }}
          >
            🍽️
          </div>
        )}
        <div className="p-4">
          <h3 className="text-[22px] font-bold leading-tight">{restaurant.name}</h3>
          {restaurant.cuisine_names.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {restaurant.cuisine_names.map((n, i) => (
                <span
                  key={n}
                  className="px-2.5 py-1 rounded-[var(--radius-full)] text-[12px] font-medium"
                  style={{
                    backgroundColor: "var(--colour-surface-raised)",
                    color: "var(--colour-text-primary)",
                  }}
                >
                  {restaurant.cuisine_emojis[i] ?? ""} {n}
                </span>
              ))}
            </div>
          )}
          <p className="mt-3 text-[14px] text-[var(--colour-text-secondary)]">
            {[restaurant.neighbourhood_name, restaurant.city_name].filter(Boolean).join(", ") ||
              restaurant.address}
          </p>
          {restaurant.price_tier && (
            <p className="mt-1 text-[14px] text-[var(--colour-text-secondary)]">
              {"£".repeat(restaurant.price_tier)}
              <span className="opacity-30">
                {"£".repeat(4 - restaurant.price_tier)}
              </span>
            </p>
          )}
        </div>
      </div>

      <div className="mt-5 space-y-2.5">
        <FButton fullWidth onClick={onConfirm}>
          Yes, I ate here
        </FButton>
        <FButton fullWidth variant="outline" onClick={onWrong}>
          Wrong place
        </FButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Step 3 — Dishes
// ---------------------------------------------------------------
function Step3Dishes({
  restaurant,
  selectedIds,
  onChange,
  suggestedNames,
  onChangeSuggestedNames,
  onNext,
  onSkip,
}: {
  restaurant: ConfirmedRestaurant;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  suggestedNames: string[];
  onChangeSuggestedNames: (names: string[]) => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  const { user } = useAuth();
  const [dishes, setDishes] = useState<DishRow[]>([]);
  const [query, setQuery] = useState("");

  // Suggest flow state
  type SuggestState =
    | { kind: "closed" }
    | { kind: "loading"; term: string }
    | { kind: "matches"; term: string; matches: { name: string; slug: string; id: string }[] }
    | { kind: "new"; term: string; value: string; submitting: boolean };
  const [suggest, setSuggest] = useState<SuggestState>({ kind: "closed" });

  useEffect(() => {
    (async () => {
      let q = supabase
        .from("dishes")
        .select("id, name, cuisine_type_id")
        .eq("status", "active");
      if (restaurant.cuisine_type_ids.length > 0) {
        q = q.in("cuisine_type_id", restaurant.cuisine_type_ids);
      }
      const { data } = await q.limit(200);
      setDishes((data ?? []) as DishRow[]);
    })();
  }, [restaurant.cuisine_type_ids]);

  const filtered = useMemo(() => {
    const t = query.trim().toLowerCase();
    if (!t) return dishes;
    return dishes.filter((d) => d.name.toLowerCase().includes(t));
  }, [dishes, query]);

  const toggle = (id: string) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id],
    );
  };

  const startSuggest = async () => {
    const term = query.trim();
    if (!term) return;
    setSuggest({ kind: "loading", term });
    const { data, error } = await supabase.rpc("match_dishes_by_similarity", {
      search_term: term,
    });
    if (error) {
      toast.error(error.message);
      setSuggest({ kind: "closed" });
      return;
    }
    const matches = ((data as any[]) ?? []).map((d) => ({
      id: d.id as string,
      name: d.name as string,
      slug: d.slug as string,
    }));
    setSuggest({ kind: "matches", term, matches });
  };

  const pickExisting = (id: string) => {
    if (!selectedIds.includes(id)) onChange([...selectedIds, id]);
    setSuggest({ kind: "closed" });
    setQuery("");
  };

  const slugify = (s: string) =>
    s
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

  const submitNew = async () => {
    if (suggest.kind !== "new") return;
    const name = suggest.value.trim().slice(0, 60);
    if (!name || !user) return;
    setSuggest({ ...suggest, submitting: true });
    // Pull the user id from a fresh session to avoid any stale/cached context
    // that could violate RLS on dishes.
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    const authedUserId = sessionData?.session?.user?.id;
    if (sessionError || !authedUserId) {
      toast.error("Please sign in again to suggest a dish");
      setSuggest({ ...suggest, submitting: false });
      return;
    }
    const slug = `${slugify(name)}-${Date.now().toString(36)}`;
    const cuisine_type_id = restaurant.cuisine_type_ids[0] ?? null;
    const { error } = await supabase.from("dishes").insert({
      name,
      slug,
      status: "suggested",
      suggested_by_user_id: authedUserId,
      cuisine_type_id,
    });

    if (error) {
      toast.error(error.message);
      setSuggest({ ...suggest, submitting: false });
      return;
    }
    onChangeSuggestedNames([...suggestedNames, name]);
    toast.success("Thanks, we'll review your suggestion");
    setSuggest({ kind: "closed" });
    setQuery("");
  };

  const removeSuggested = (name: string) => {
    onChangeSuggestedNames(suggestedNames.filter((n) => n !== name));
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="px-5 shrink-0">
        <h3 className="text-[22px] font-bold">What did you eat?</h3>
        <p className="text-[14px] text-[var(--colour-text-secondary)] mt-1">
          Optional — skip if you like
        </p>
      </div>

      <div className="px-4 mt-4 shrink-0">
        <div className="relative">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--colour-text-secondary)]"
          />
          <FInput
            name="dish_search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search dishes"
            className="pl-9"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 mt-4 pb-4">
        <div className="flex flex-wrap gap-2">
          {filtered.map((d) => {
            const active = selectedIds.includes(d.id);
            return (
              <button
                key={d.id}
                onClick={() => toggle(d.id)}
                className="px-3.5 min-h-[36px] rounded-[var(--radius-full)] text-[13px] font-medium border transition-colors"
                style={{
                  backgroundColor: active
                    ? "var(--colour-accent)"
                    : "var(--colour-surface)",
                  color: active
                    ? "var(--colour-text-inverse)"
                    : "var(--colour-text-primary)",
                  borderColor: active
                    ? "var(--colour-accent)"
                    : "var(--colour-border)",
                }}
              >
                {d.name}
              </button>
            );
          })}
          {suggestedNames.map((n) => (
            <button
              key={`sug-${n}`}
              onClick={() => removeSuggested(n)}
              className="px-3.5 min-h-[36px] rounded-[var(--radius-full)] text-[13px] font-medium border inline-flex items-center gap-1.5"
              style={{
                backgroundColor: "var(--colour-surface-raised)",
                color: "var(--colour-text-secondary)",
                borderColor: "var(--colour-border)",
                opacity: 0.7,
              }}
              title="Pending review — tap to remove"
            >
              {n}
              <X size={12} />
            </button>
          ))}
        </div>

        {filtered.length === 0 && query.trim() && (
          <div className="flex flex-col items-start gap-3 py-6">
            <p className="text-[14px] text-[var(--colour-text-secondary)]">
              No dishes match.
            </p>
            <FButton variant="outline" onClick={startSuggest}>
              Suggest a dish
            </FButton>
          </div>
        )}
        {filtered.length === 0 && !query.trim() && suggestedNames.length === 0 && (
          <p className="text-[14px] text-[var(--colour-text-secondary)] py-6">
            No dishes available.
          </p>
        )}
      </div>

      <div className="px-4 pt-3 pb-4 border-t shrink-0 flex items-center justify-between gap-3"
        style={{ borderColor: "var(--colour-border)" }}>
        <button
          onClick={onSkip}
          className="text-[14px] text-[var(--colour-text-secondary)] min-h-[44px] px-2"
        >
          Skip
        </button>
        <FButton onClick={onNext} className="px-8">
          Next
        </FButton>
      </div>

      {suggest.kind !== "closed" && (
        <SuggestSheet
          state={suggest}
          onClose={() => setSuggest({ kind: "closed" })}
          onPick={pickExisting}
          onGoNew={() =>
            setSuggest({ kind: "new", term: suggest.term, value: suggest.term, submitting: false })
          }
          onChangeNewValue={(v) =>
            suggest.kind === "new" && setSuggest({ ...suggest, value: v.slice(0, 60) })
          }
          onSubmitNew={submitNew}
        />
      )}
    </div>
  );
}

function SuggestSheet({
  state,
  onClose,
  onPick,
  onGoNew,
  onChangeNewValue,
  onSubmitNew,
}: {
  state:
    | { kind: "loading"; term: string }
    | { kind: "matches"; term: string; matches: { name: string; slug: string; id: string }[] }
    | { kind: "new"; term: string; value: string; submitting: boolean };
  onClose: () => void;
  onPick: (id: string) => void;
  onGoNew: () => void;
  onChangeNewValue: (v: string) => void;
  onSubmitNew: () => void;
}) {
  return (
    <div className="absolute inset-0 z-10 flex items-end">
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />
      <div
        className="relative w-full p-5 animate-in slide-in-from-bottom duration-200"
        style={{
          backgroundColor: "var(--colour-surface)",
          borderTopLeftRadius: "var(--radius-lg)",
          borderTopRightRadius: "var(--radius-lg)",
          paddingBottom: "calc(env(safe-area-inset-bottom) + 1.25rem)",
        }}
      >
        {state.kind === "loading" && (
          <p className="text-[14px] text-[var(--colour-text-secondary)] py-6 text-center">
            Looking for similar dishes…
          </p>
        )}

        {state.kind === "matches" && (
          <>
            <h3 className="text-[18px] font-bold mb-3">
              {state.matches.length > 0 ? "Did you mean one of these?" : "No similar dishes"}
            </h3>
            {state.matches.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {state.matches.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => onPick(m.id)}
                    className="px-3.5 min-h-[36px] rounded-[var(--radius-full)] text-[13px] font-medium border"
                    style={{
                      backgroundColor: "var(--colour-surface)",
                      color: "var(--colour-text-primary)",
                      borderColor: "var(--colour-border)",
                    }}
                  >
                    {m.name}
                  </button>
                ))}
              </div>
            )}
            <FButton fullWidth variant="outline" onClick={onGoNew}>
              No, it's something new
            </FButton>
          </>
        )}

        {state.kind === "new" && (
          <>
            <h3 className="text-[18px] font-bold mb-3">What's the dish called?</h3>
            <FInput
              name="new_dish_name"
              value={state.value}
              onChange={(e) => onChangeNewValue(e.target.value)}
              placeholder="Dish name"
              maxLength={60}
              autoFocus
            />
            <div className="text-right text-[12px] text-[var(--colour-text-secondary)] mt-1 mb-3">
              {state.value.length}/60
            </div>
            <FButton
              fullWidth
              onClick={onSubmitNew}
              disabled={state.submitting || !state.value.trim()}
            >
              {state.submitting ? "Submitting…" : "Suggest"}
            </FButton>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Step 4 — Photo + caption + log
// ---------------------------------------------------------------
function Step4Photo({
  photoFile,
  setPhotoFile,
  caption,
  setCaption,
  restaurant,
  dishIds,
  suggestedDishNames,
  onLogged,
}: {
  photoFile: File | null;
  setPhotoFile: (f: File | null) => void;
  caption: string;
  setCaption: (c: string) => void;
  restaurant: ConfirmedRestaurant;
  dishIds: string[];
  suggestedDishNames: string[];
  onLogged: (visitId: string) => void;
}) {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const previewUrl = useMemo(
    () => (photoFile ? URL.createObjectURL(photoFile) : null),
    [photoFile],
  );
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setPhotoFile(f);
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const sessionUserId = sessionData.session?.user.id;
      if (!sessionUserId) {
        toast.error("Please sign in again");
        window.location.assign("/signin");
        return;
      }

      let photo_urls: string[] = [];
      if (photoFile) {
        const ext = photoFile.name.split(".").pop() ?? "jpg";
        const path = `${sessionUserId}/${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("visit-photos")
          .upload(path, photoFile, { upsert: false, contentType: photoFile.type });
        if (upErr) {
          // Bucket may not exist yet — log but don't fail the whole flow.
          console.warn("Photo upload failed", upErr.message);
        } else {
          const { data: pub } = supabase.storage.from("visit-photos").getPublicUrl(path);
          if (pub?.publicUrl) photo_urls = [pub.publicUrl];
        }
      }

      const { data: inserted, error } = await supabase
        .from("visits")
        .insert({
          user_id: sessionUserId,
          restaurant_id: restaurant.id,
          detection_method: "manual",
          photo_urls,
          dish_ids: dishIds,
          suggested_dish_names: suggestedDishNames,
          caption: caption.trim() || null,
          is_public: true,
        })
        .select("id")
        .single();
      if (error) throw error;
      onLogged(inserted.id);
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to log visit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="px-5 shrink-0">
        <h3 className="text-[22px] font-bold">Add a photo</h3>
        <p className="text-[14px] text-[var(--colour-text-secondary)] mt-1">
          Optional — skip if you like
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-4 mt-4 pb-4">
        {!photoFile ? (
          <div
            className="w-full aspect-[4/3] rounded-[var(--radius-lg)] border-2 border-dashed flex flex-col items-center justify-center gap-3 px-6"
            style={{
              borderColor: "var(--colour-border)",
              backgroundColor: "var(--colour-surface-raised)",
            }}
          >
            <Camera size={32} className="text-[var(--colour-text-secondary)]" />
            <span className="text-[14px] text-[var(--colour-text-secondary)] text-center">
              Add a photo of your meal
            </span>
            <div className="flex flex-col sm:flex-row gap-2 w-full max-w-xs">
              <label
                className="flex-1 inline-flex items-center justify-center gap-2 h-11 px-4 rounded-[var(--radius-md)] text-[14px] font-semibold cursor-pointer border"
                style={{
                  backgroundColor: "var(--colour-surface)",
                  borderColor: "var(--colour-border)",
                  color: "var(--colour-text-primary)",
                }}
              >
                <Camera size={16} />
                Take Photo
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={onPick}
                />
              </label>
              <label
                className="flex-1 inline-flex items-center justify-center gap-2 h-11 px-4 rounded-[var(--radius-md)] text-[14px] font-semibold cursor-pointer border"
                style={{
                  backgroundColor: "var(--colour-surface)",
                  borderColor: "var(--colour-border)",
                  color: "var(--colour-text-primary)",
                }}
              >
                <ImageIcon size={16} />
                Choose from Library
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onPick}
                />
              </label>
            </div>
          </div>
        ) : (
          <div className="relative rounded-[var(--radius-lg)] overflow-hidden">
            <img
              src={previewUrl!}
              alt="Selected"
              className="w-full aspect-[4/3] object-cover"
            />
            <button
              onClick={() => setPhotoFile(null)}
              aria-label="Remove photo"
              className="absolute top-2 right-2 w-9 h-9 rounded-[var(--radius-full)] bg-black/60 text-white flex items-center justify-center"
            >
              <X size={18} />
            </button>
          </div>
        )}

        <div className="mt-4">
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, 280))}
            placeholder="Say something about it..."
            rows={3}
            className="w-full p-3 rounded-[var(--radius-md)] border resize-none text-[15px] outline-none focus:ring-2"
            style={{
              borderColor: "var(--colour-border)",
              backgroundColor: "var(--colour-surface)",
              color: "var(--colour-text-primary)",
            }}
          />
          <div className="text-right text-[12px] text-[var(--colour-text-secondary)] mt-1">
            {caption.length}/280
          </div>
        </div>
      </div>

      <div className="px-4 pt-3 pb-4 border-t shrink-0 flex items-center justify-between gap-3"
        style={{ borderColor: "var(--colour-border)" }}>
        <button
          onClick={submit}
          disabled={submitting}
          className="text-[14px] text-[var(--colour-text-secondary)] min-h-[44px] px-2 disabled:opacity-50"
        >
          Skip
        </button>
        <FButton onClick={submit} disabled={submitting} className="px-8">
          {submitting ? "Logging…" : "Log It"}
        </FButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Step 5 — Result: engine + reveal + explicit share prompt
// ---------------------------------------------------------------
function Step5Result({
  visitId,
  restaurant,
  dishIds,
  onDone,
}: {
  visitId: string;
  restaurant: ConfirmedRestaurant;
  dishIds: string[];
  photoUrlsHint: null;
  onDone: () => void;
}) {
  const { user } = useAuth();
  const [phase, setPhase] = useState<
    "checking" | "reveal" | "share" | "posting" | "done"
  >("checking");
  const [badges, setBadges] = useState<UserAchievement[]>([]);
  const [postContent, setPostContent] = useState("");
  const [postPhotoUrls, setPostPhotoUrls] = useState<string[]>([]);
  const [isPublic, setIsPublic] = useState(true);

  useEffect(() => {
    if (!user) return;
    const minDelay = new Promise((r) => setTimeout(r, 500));
    (async () => {
      try {
        const [result] = await Promise.all([
          checkAchievements(visitId, user.id),
          minDelay,
        ]);
        setBadges(result);

        // Prepare (but do NOT auto-post) feed post content
        const [{ data: visit }, { data: dishRows }, { data: profile }] = await Promise.all([
          supabase.from("visits").select("photo_urls").eq("id", visitId).maybeSingle(),
          dishIds.length > 0
            ? supabase.from("dishes").select("name").in("id", dishIds)
            : Promise.resolve({ data: [] as { name: string }[] }),
          supabase.from("profiles").select("privacy_setting").eq("id", user.id).maybeSingle(),
        ]);
        const dishNames = (dishRows ?? []).map((d: any) => d.name);
        let content = `Just ate at ${restaurant.name} 🍴`;
        if (dishNames.length > 0) content += ` — ${dishNames.slice(0, 3).join(", ")}`;
        if (result.length > 0)
          content += ` and earned ${result.length} badge${result.length > 1 ? "s" : ""}!`;
        setPostContent(content);
        setPostPhotoUrls(((visit as any)?.photo_urls ?? []) as string[]);
        setIsPublic((profile as any)?.privacy_setting !== "private");

        setPhase(result.length > 0 ? "reveal" : "share");
      } catch (e: any) {
        toast.error(e?.message ?? "Something went wrong");
        setPhase("share");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitId, user?.id]);

  const handleShare = async () => {
    if (!user) return;
    setPhase("posting");
    // Always pull photo_urls directly from the visit so the feed post matches
    // the photos the user uploaded, regardless of any earlier state.
    const { data: visitRow } = await supabase
      .from("visits")
      .select("photo_urls")
      .eq("id", visitId)
      .maybeSingle();
    const photoUrls =
      (((visitRow as any)?.photo_urls ?? postPhotoUrls) as string[]) ?? [];
    const { error } = await supabase.from("feed_posts").insert({
      user_id: user.id,
      visit_id: visitId,
      content: postContent,
      photo_urls: photoUrls,
      achievement_ids: badges.map((b) => b.achievement_id),
      is_public: isPublic,
    });
    if (error) {
      toast.error(error.message);
      setPhase("share");
      return;
    }
    toast.success("Shared to your feed");
    setPhase("done");
    onDone();
  };

  const handleSkip = () => {
    setPhase("done");
    onDone();
  };

  if (phase === "checking") {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-10">
        <div
          className="w-20 h-20 rounded-[var(--radius-full)] flex items-center justify-center mb-5"
          style={{ backgroundColor: "var(--colour-surface-raised)" }}
        >
          <span className="text-[40px] animate-pulse">🍴</span>
        </div>
        <p className="text-[15px] text-[var(--colour-text-secondary)]">
          Checking your achievements…
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-10">
        <div
          className="w-20 h-20 rounded-[var(--radius-full)] flex items-center justify-center mb-5 animate-in zoom-in duration-300"
          style={{ backgroundColor: "var(--colour-accent)" }}
        >
          <Check size={42} className="text-[var(--colour-text-inverse)]" strokeWidth={3} />
        </div>
        <h3 className="text-[22px] font-bold mb-1">{restaurant.name}</h3>
        <p className="text-[15px] text-[var(--colour-text-secondary)] mb-8">
          {badges.length === 0
            ? "Logged! Keep exploring to earn badges."
            : "Logged!"}
        </p>

        {phase === "share" || phase === "posting" ? (
          <div className="w-full max-w-xs">
            <p className="text-[16px] font-semibold mb-4">Share to your feed?</p>
            <div className="flex flex-col gap-2.5">
              <FButton
                fullWidth
                onClick={handleShare}
                disabled={phase === "posting"}
              >
                {phase === "posting" ? "Sharing…" : "Share"}
              </FButton>
              <FButton
                variant="outline"
                fullWidth
                onClick={handleSkip}
                disabled={phase === "posting"}
              >
                Skip
              </FButton>
            </div>
          </div>
        ) : (
          <FButton onClick={onDone} className="px-10">
            Done
          </FButton>
        )}
      </div>
      {phase === "reveal" && (
        <BadgeReveal badges={badges} onDone={() => setPhase("share")} />
      )}
    </>
  );
}

// ---------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------
async function fetchCuisineIdsForRestaurant(restaurantId: string): Promise<string[]> {
  const { data } = await supabase
    .from("restaurant_cuisine_types")
    .select("cuisine_type_id")
    .eq("restaurant_id", restaurantId);
  return (data ?? []).map((r: any) => r.cuisine_type_id);
}

async function fetchRestaurantMeta(restaurantId: string): Promise<{
  city_name: string | null;
  neighbourhood_name: string | null;
  cuisine_names: string[];
  cuisine_emojis: string[];
}> {
  const { data: r } = await supabase
    .from("restaurants")
    .select(
      "city:cities(name), neighbourhood:neighbourhoods(name), restaurant_cuisine_types(cuisine_types(name, emoji, display_order))",
    )
    .eq("id", restaurantId)
    .maybeSingle();
  const rels = (r as any)?.restaurant_cuisine_types ?? [];
  const cts = rels
    .map((x: any) => x.cuisine_types)
    .filter(Boolean)
    .sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0));
  return {
    city_name: (r as any)?.city?.name ?? null,
    neighbourhood_name: (r as any)?.neighbourhood?.name ?? null,
    cuisine_names: cts.map((c: any) => c.name),
    cuisine_emojis: cts.map((c: any) => c.emoji ?? ""),
  };
}
